import { NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { ImageModule } from '@df/module-image/image.module';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs';
import { NBuilderConfig } from '../../common/builder.config';
import { BuilderSimpleStepComponent } from '../../common/simple-step/simple-step.component';
import { NOutfitBuilder } from '../outfit-builder.namespace';
import { OutfitBuilderService } from '../outfit-builder.service';
import { NOutfitGeneralSetup } from './outfit-general-setup.config';

@Component({
    selector: 'outfit-general-setup',
    templateUrl: './outfit-general-setup.html',
    styleUrls: ['./outfit-general-setup.scss'],
    standalone: true,
    imports: [
        BuilderSimpleStepComponent,
        DfUiModule,
        Ga4TrackClickDirective,
        ICmsClientModule,
        ImageModule,
        NgFor,
        NgIf,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutfitGeneralSetupComponent implements OnChanges, OnDestroy {
    @Input()
    content: NOutfitGeneralSetup.IGeneralSetupData;
    @Input()
    storageData: NOutfitBuilder.IStorageData;
    protected readonly ESteps = NOutfitBuilder.ESteps;
    protected readonly EOptions = NOutfitBuilder.EConfigureOptions;
    protected horseStepData: NBuilderConfig.ISimpleStep;
    protected riderStepData: NBuilderConfig.ISimpleStep;
    private clientSubscription: Subscription;

    constructor(
        private outfitBuilderService: OutfitBuilderService,
        private events: Events,
        private changeDetectorRef: ChangeDetectorRef,
        private client: Client
    ) {
        this.clientSubscription = this.client.obs.breakpoint.subscribe(() => this.changeDetectorRef.detectChanges());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['storageData']) {
            const configOptionIndex = this.content.firstStepOptions.findIndex(option => option.preselect);
            if (this.storageData.configOption === undefined) {
                this.storageData.configOption = this.getConfigOption(configOptionIndex >= 0 ? configOptionIndex : 0);
            }
        }
        if (changes['content']) {
            // Rider step content
            this.riderStepData = {
                title: this.content.riderStepTitle,
                tabs: [
                    {
                        title: 'Women',
                        id: NOutfitGeneralSetup.EGender.women,
                        options: this.content.riderStepWomenOptions
                    },
                    { title: 'Men', id: NOutfitGeneralSetup.EGender.men, options: this.content.riderStepMenOptions }
                ].filter(tab => tab.options.length > 0)
            };
            // Horse step content
            this.horseStepData = {
                title: this.content.horseStepTitle,
                tabs: [
                    {
                        title: 'Horse',
                        id: NOutfitGeneralSetup.EHorseType.horse,
                        options: this.content.horseStepHorseOptions
                    },
                    { title: 'Pony', id: NOutfitGeneralSetup.EHorseType.pony, options: this.content.horseStepPonyOptions }
                ].filter(tab => tab.options.length > 0)
            };
        }
        this.changeDetectorRef.detectChanges();
    }
    ngOnDestroy(): void {
        this.clientSubscription?.unsubscribe();
    }
    protected next() {
        if (this.isDataValid()) {
            this.storageData.step = this.getNextStep();
            this.updateStorage();
        }
    }
    protected back() {
        const prevStep = this.outfitBuilderService.getPrevStep(this.storageData);
        if (
            (prevStep === this.ESteps.rider && this.invalidSimpleStep(this.riderStepData)) ||
            (prevStep === this.ESteps.horse && this.invalidSimpleStep(this.horseStepData))
        ) {
            this.storageData.step = prevStep;
            this.back();
        } else {
            this.outfitBuilderService.goToStep(this.storageData, prevStep);
        }
    }
    protected selectFirstStep(index: number) {
        // select new option
        this.storageData.configOption = this.getConfigOption(index);
        // remove redundant options if needed
        if (this.storageData.configOption === this.EOptions.rider) {
            this.storageData.horse = undefined;
        }
        if (this.storageData.configOption === this.EOptions.horse) {
            this.storageData.rider = undefined;
        }
        this.updateStorage();
    }
    // need by index cause name is editable in icms
    protected getConfigOption(index: number): NOutfitBuilder.EConfigureOptions {
        return [this.EOptions.riderAndHorse, this.EOptions.rider, this.EOptions.horse][index];
    }
    protected isDataValid(): boolean {
        return this.outfitBuilderService.validateData(this.storageData, this.content);
    }
    protected updateStorage(): void {
        this.changeDetectorRef.detectChanges();
        this.events.broadcast(NBuilderConfig.updateStorageEvent);
    }
    private invalidSimpleStep(step: NBuilderConfig.ISimpleStep): boolean {
        return step.tabs.length === 1 && step.tabs[0].options.length === 1;
    }
    private getNextStep(): NOutfitBuilder.ESteps {
        const steps = NOutfitBuilder.STEPS_CONFIG[this.storageData.configOption];
        const indexOfCurrentStep = steps.findIndex(e => e === this.storageData.step);
        let nexStepIndex = steps[Math.min(steps.length - 1, indexOfCurrentStep + 1)];

        // if there is only one option for rider pick it and skip that step
        if (nexStepIndex === this.ESteps.rider && this.invalidSimpleStep(this.riderStepData)) {
            this.storageData.step = this.ESteps.rider;
            this.storageData.rider = { tabId: this.riderStepData.tabs[0].id, optionName: this.riderStepData.tabs[0].options[0].name };
            this.updateStorage();
            return this.getNextStep();
        }
        // if there is only one option for horse pick it and skip that step
        if (nexStepIndex === this.ESteps.horse && this.invalidSimpleStep(this.horseStepData)) {
            this.storageData.step = this.ESteps.horse;
            this.storageData.horse = { tabId: this.horseStepData.tabs[0].id, optionName: this.horseStepData.tabs[0].options[0].name };
            this.updateStorage();
            return this.getNextStep();
        }
        return nexStepIndex;
    }
    protected getGA4Data(): Gtm.IGA4TrackData {
        switch (this.storageData.step) {
            case this.ESteps.configure:
                return {
                    name: 'ob_visualisation_mode_chosen',
                    details: { mode: this.storageData.configOption }
                };
            case this.ESteps.rider:
                return {
                    name: 'ob_model_chosen',
                    details: {
                        entityType: this.EOptions.rider,
                        modelID: `${optionLabelPipe(this.storageData.rider.tabId, 'gender')}-${this.storageData.rider.optionName}`
                    }
                };
            case this.ESteps.horse:
                return {
                    name: 'ob_model_chosen',
                    details: {
                        entityType: this.EOptions.horse,
                        modelID: `${optionLabelPipe(this.storageData.horse.tabId, 'horse_type')}-${this.storageData.horse.optionName}`
                    }
                };
        }
    }
}
